/**
 * OrgContext — Organization Hierarchy Context
 *
 * Provides the org node tree, sites list, and active site selection
 * to the entire app. Backed by /api/org/nodes and /api/org/sites.
 *
 * Phase 0 — Multi-Site Foundation
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const OrgContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

// Valid node type hierarchy (parent → allowed children)
// "other" is a flexible intermediate layer with a user-defined custom_label
export const NODE_TYPE_CHILDREN = {
  organization: ['region', 'country', 'business_unit', 'other', 'site'],
  region: ['country', 'other', 'site'],
  country: ['business_unit', 'other', 'site'],
  business_unit: ['other', 'site'],
  other: ['region', 'country', 'business_unit', 'other', 'site'],
  site: ['sub_site', 'production_line'],
  sub_site: ['production_line'],
  production_line: [],
};

export const NODE_TYPE_LABELS = {
  organization: 'Organization',
  region: 'Region',
  country: 'Country',
  business_unit: 'Business Unit',
  other: 'Custom',
  site: 'Site',
  sub_site: 'Sub-Site',
  production_line: 'Production Line',
};

export const NODE_TYPE_COLORS = {
  organization: 'bg-purple-100 text-purple-800',
  region: 'bg-blue-100 text-blue-800',
  country: 'bg-cyan-100 text-cyan-800',
  business_unit: 'bg-indigo-100 text-indigo-800',
  other: 'bg-amber-100 text-amber-800',
  site: 'bg-green-100 text-green-800',
  sub_site: 'bg-teal-100 text-teal-800',
  production_line: 'bg-orange-100 text-orange-800',
};

/**
 * Get the display label for a node, resolving custom_label for "other" type.
 */
export function getNodeDisplayLabel(node) {
  if (node.display_label) return node.display_label;
  if (node.node_type === 'other' && node.custom_label) return node.custom_label;
  return NODE_TYPE_LABELS[node.node_type] ?? node.node_type;
}

export function OrgProvider({ children }) {
  // Flat list of all org nodes (tree is built from this in the component)
  const [nodes, setNodes] = useState([]);
  // Flat list of site-type nodes only
  const [sites, setSites] = useState([]);
  // Currently selected site (null = corporate / all-sites view). The persisted
  // value uses the '__ALL__' sentinel for an explicit all-sites selection
  // (shared convention with SiteContext); treat it — and any absent value — as null.
  const [activeSiteId, setActiveSiteIdState] = useState(() => {
    const stored = localStorage.getItem('activeSiteId');
    return stored && stored !== '__ALL__' ? stored : null;
  });
  const [activeSiteObj, setActiveSiteObj] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { token } = useAuth();

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  // Fetch entire org tree for this tenant
  const fetchNodes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/org/nodes`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed to load org hierarchy (${res.status})`);
      const data = await res.json();
      setNodes(Array.isArray(data) ? data : data.nodes ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders]);

  // Fetch flat list of site-type nodes
  const fetchSites = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/org/sites`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed to load sites (${res.status})`);
      const data = await res.json();
      const siteList = Array.isArray(data) ? data : data.sites ?? [];
      setSites(siteList);
    } catch (err) {
      console.error('[OrgContext] fetchSites:', err.message);
    }
  }, [token, authHeaders]);

  // Load on mount / token change
  useEffect(() => {
    if (token) {
      fetchNodes();
      fetchSites();
    }
  }, [token, fetchNodes, fetchSites]);

  // Keep activeSite object in sync when sites list or activeSiteId changes
  useEffect(() => {
    if (activeSiteId && sites.length > 0) {
      setActiveSiteObj(sites.find((s) => s.id === activeSiteId) ?? null);
    } else {
      setActiveSiteObj(null);
    }
  }, [activeSiteId, sites]);

  // Switch active site (persisted in localStorage). Persist null as the
  // '__ALL__' sentinel — matching SiteContext — so an explicit all-sites
  // selection survives a refresh instead of being wiped (which previously let
  // the stale JWT active_site_id revert the view to a specific site).
  const setActiveSite = useCallback((siteId) => {
    setActiveSiteIdState(siteId);
    localStorage.setItem('activeSiteId', siteId || '__ALL__');
  }, []);

  // ── CRUD Operations ──────────────────────────────────────────────────────

  const createNode = useCallback(async (payload) => {
    const res = await fetch(`${API_BASE_URL}/org/nodes`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create node');
    }
    const created = await res.json();
    await fetchNodes();
    await fetchSites();
    return created;
  }, [authHeaders, fetchNodes, fetchSites]);

  const updateNode = useCallback(async (id, payload) => {
    const res = await fetch(`${API_BASE_URL}/org/nodes/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update node');
    }
    const updated = await res.json();
    await fetchNodes();
    await fetchSites();
    return updated;
  }, [authHeaders, fetchNodes, fetchSites]);

  // Advisory: how many operational records / users a soft-delete would orphan.
  const getNodeDeletionImpact = useCallback(async (id) => {
    const res = await fetch(`${API_BASE_URL}/org/nodes/${id}/deletion-impact`, {
      headers: authHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load deletion impact');
    }
    return res.json();
  }, [authHeaders]);

  const deleteNode = useCallback(async (id, { force = false } = {}) => {
    const url = force
      ? `${API_BASE_URL}/org/nodes/${id}?force=true`
      : `${API_BASE_URL}/org/nodes/${id}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to delete node');
    }
    await fetchNodes();
    await fetchSites();
  }, [authHeaders, fetchNodes, fetchSites]);

  const moveNode = useCallback(async (id, newParentId) => {
    const res = await fetch(`${API_BASE_URL}/org/nodes/${id}/move`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ new_parent_id: newParentId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to move node');
    }
    await fetchNodes();
  }, [authHeaders, fetchNodes]);

  // ── Wizard bulk creation ─────────────────────────────────────────────────

  const runSetupWizard = useCallback(async (payload) => {
    const res = await fetch(`${API_BASE_URL}/org/setup-wizard`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Setup wizard failed');
    }
    const result = await res.json();
    await fetchNodes();
    await fetchSites();
    return result;
  }, [authHeaders, fetchNodes, fetchSites]);

  // ── Utility: build nested tree from flat node list ────────────────────────

  const buildTree = useCallback((flatNodes) => {
    const map = {};
    const roots = [];
    flatNodes.forEach((n) => { map[n.id] = { ...n, children: [] }; });
    flatNodes.forEach((n) => {
      if (n.parent_id && map[n.parent_id]) {
        map[n.parent_id].children.push(map[n.id]);
      } else {
        roots.push(map[n.id]);
      }
    });
    return roots;
  }, []);

  const tree = buildTree(nodes);

  const value = {
    // Raw data
    nodes,
    sites,
    tree,
    // Active site selection
    activeSiteId,
    activeSite: activeSiteObj,
    setActiveSite,
    // Loading / error
    loading,
    error,
    // CRUD
    createNode,
    updateNode,
    deleteNode,
    getNodeDeletionImpact,
    moveNode,
    // Wizard
    runSetupWizard,
    // Manual refresh
    refreshTree: fetchNodes,
    refreshSites: fetchSites,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return ctx;
}
